/**
 * ListenToArticle — "Listen ›" button that lazily generates an MP3 via
 * OpenAI TTS and streams it back as an inline <audio> player.
 *
 * States:
 *   idle      → button says "Listen ›"
 *   loading   → spinner while /generate is running (first play only)
 *   ready     → shows a native audio player with the mp3_url
 *   error     → inline warning
 *
 * Reuses the same cache the backend maintains, so repeat clicks are
 * instant. Auto-invalidates when the article body changes.
 */
import { useCallback, useEffect, useState } from "react";
import { Headphones, Loader2, AlertCircle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function ListenToArticle({ slug }) {
  const [state, setState] = useState({ status: "unknown", meta: null, error: null });

  // Probe the cache on mount — instant player if it's already there
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/article/${slug}/tts/status`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!alive) return;
        setState(d.ready
          ? { status: "ready", meta: d, error: null }
          : { status: "idle", meta: null, error: null });
      } catch {
        if (alive) setState({ status: "idle", meta: null, error: null });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const generate = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const r = await fetch(`${BASE}/article/${slug}/tts/generate`, { method: "POST" });
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setState({ status: "ready", meta: d, error: null });
    } catch (e) {
      setState({ status: "error", meta: null, error: e.message || "Could not generate audio." });
    }
  }, [slug]);

  if (state.status === "ready" && state.meta) {
    const durMin = state.meta.duration_s
      ? Math.max(1, Math.round(state.meta.duration_s / 60))
      : null;
    return (
      <div
        className="my-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
        data-testid="article-tts-player"
      >
        <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
          <Headphones className="w-3.5 h-3.5" />
          Listen to this story
          {durMin && <span className="text-slate-400 normal-case tracking-normal">· {durMin} min</span>}
        </div>
        <audio
          controls
          preload="none"
          src={`${process.env.REACT_APP_BACKEND_URL || ""}${state.meta.mp3_url}`}
          className="w-full"
          data-testid="article-tts-audio"
        >
          Your browser does not support the audio tag.
        </audio>
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <button
        type="button"
        disabled
        className="my-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-500 text-sm font-medium"
        data-testid="article-tts-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Generating audio…
      </button>
    );
  }

  if (state.status === "error") {
    return (
      <div className="my-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 text-amber-800 text-sm border border-amber-200" data-testid="article-tts-error">
        <AlertCircle className="w-4 h-4" />
        {state.error}
        <button
          type="button"
          onClick={generate}
          className="ml-2 underline hover:no-underline"
          data-testid="article-tts-retry"
        >
          Try again
        </button>
      </div>
    );
  }

  // idle / unknown
  return (
    <button
      type="button"
      onClick={generate}
      className="my-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors"
      data-testid="article-tts-listen"
    >
      <Headphones className="w-4 h-4" />
      Listen to this story ›
    </button>
  );
}
